import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Factory, MapPin, Phone } from "lucide-react";

// Countertop & surface materials sourced through our showroom partnership with
// Dream Stoneworks (168 Main St, Northborough) — fabricated in-house in Hudson, MA.
const MATERIALS = [
  {
    id: "quartz",
    name: "Quartz",
    kicker: "Engineered Stone",
    image: "/photos/materials/quartz.jpg",
    headline: "Low maintenance, consistent pattern, busy-kitchen proof.",
    blurb:
      "Engineered stone combining natural quartz with resins. Non-porous, never needs sealing, and made for daily use. The top choice for busy New England kitchens, rentals, and homeowners who want consistent pattern across every slab.",
    benefits: [
      "Non-porous — resists bacteria, mold, and staining without sealing",
      "Exceptional durability for daily use",
      "Low maintenance — soap and water cleaning",
      "Consistent pattern across the entire countertop",
      "Eco-friendly options with recycled content",
    ],
    brandsLabel: "Premium brands carried",
    brands: ["Cambria", "Caesarstone", "Silestone", "MSI Quartz", "Cosentino", "LG Viatera", "Vadara"],
  },
  {
    id: "granite",
    name: "Granite",
    kicker: "Natural Stone",
    image: "/photos/materials/granite.jpg",
    headline: "Natural movement, heat resistant, unique to your home.",
    blurb:
      "Naturally heat-resistant with unique patterns and exceptional durability. Sealed annually, granite resists stains and bacteria — perfect for busy kitchens, statement islands, outdoor kitchens, and bar tops where natural variation is part of the design.",
    benefits: [
      "Heat-resistant — set hot pots directly on the surface",
      "One-of-a-kind movement on every slab",
      "Withstands daily prep, knife work, and family life",
      "Holds resale value as a premium natural finish",
    ],
    brandsLabel: "Popular colors on the floor",
    brands: [
      "Black Galaxy",
      "Uba Tuba",
      "Santa Cecilia",
      "Giallo Ornamental",
      "White Ice",
      "Blue Pearl",
      "Tan Brown",
      "Absolute Black",
    ],
  },
  {
    id: "marble",
    name: "Marble",
    kicker: "Natural Stone",
    image: "/photos/materials/marble.jpg",
    headline: "Timeless elegance with distinctive veining.",
    blurb:
      "The ultimate choice for luxury bathrooms, statement vanities, butler's pantries, and fireplace surrounds. Softer than granite — handle with intention and a sealed finish, and you get a finish that ages into something even better than the day it was installed.",
    benefits: [
      "Bright, cool surface with one-of-a-kind veining",
      "Made for primary baths, vanities, and statement millwork",
      "Pairs with brass, black, and brushed-nickel hardware",
      "Patinas gracefully — built for forever homes",
    ],
    brandsLabel: "Favorite slabs we source",
    brands: ["Calacatta Gold", "Carrara", "Statuario", "Calacatta Viola", "Calacatta Borghini"],
  },
  {
    id: "quartzite",
    name: "Quartzite",
    kicker: "Natural Stone",
    image: "/photos/materials/quartzite.jpg",
    headline: "Harder than granite. The look of marble.",
    blurb:
      "Premium natural stone with the dramatic movement of marble and the durability of granite. The right pick for custom islands, waterfall edges, and focal-point kitchens where you want the surface to be the centerpiece.",
    benefits: [
      "Exceptional heat and scratch resistance",
      "Strong, dramatic movement for waterfall islands",
      "Ideal for focal-point kitchens and large slabs",
      "Pairs with both modern and classic cabinetry",
    ],
    brandsLabel: "Stunning slabs we love",
    brands: ["Taj Mahal", "Super White", "Sea Pearl", "Macaubas", "Cristallo", "Mont Blanc"],
  },
  {
    id: "dekton",
    name: "Dekton",
    kicker: "Ultra-Compact Sintered Surface",
    image: "/photos/materials/dekton.jpg",
    headline: "Indoor or outdoor. UV-proof. Practically indestructible.",
    blurb:
      "Dekton by Cosentino — an ultra-compact sintered surface with exceptional scratch, stain, and UV resistance. The only countertop material we recommend for full outdoor kitchens, pool decks, and façades. Wipes clean with a damp cloth.",
    benefits: [
      "UV-resistant — won't fade outdoors",
      "Scratch and stain proof",
      "Extreme heat tolerance — no trivet required",
      "Wraps around façades, floors, and outdoor counters",
    ],
    brandsLabel: "By Cosentino · finishes we stock",
    brands: ["Kelya", "Aura 15", "Halo", "Domoos", "Sirius", "Trilium"],
  },
  {
    id: "neolith",
    name: "Neolith",
    kicker: "Sintered Stone",
    image: "/photos/materials/neolith.jpg",
    headline: "Architectural-grade sintered stone for any surface.",
    blurb:
      "Ultra-compact sintered surface known for extreme durability and modern architectural design. Used not just for countertops but for full feature walls, fireplace surrounds, vanities, and outdoor applications across contemporary Massachusetts homes.",
    benefits: [
      "Massive slabs — fewer seams, more drama",
      "Engineered for countertops, walls, and outdoor builds",
      "Extreme heat, scratch, and UV resistance",
      "Made from 100% natural raw materials",
    ],
    brandsLabel: "Most-requested finishes",
    brands: ["Calacatta", "Estatuario", "Pietra di Luna", "Iron Grey", "Beton", "Sofia Cuprum"],
  },
];

const BEST_FITS = [
  {
    label: "Best for low maintenance",
    material: "Quartz",
    note: "A strong fit for busy family kitchens, rentals, and homeowners who don't want to seal.",
    anchor: "quartz",
  },
  {
    label: "Best for natural movement",
    material: "Granite",
    note: "Heat resistance, unique slab character, and a strong choice for outdoor kitchens.",
    anchor: "granite",
  },
  {
    label: "Best for statement islands",
    material: "Quartzite",
    note: "Premium natural stone with strong movement — built for waterfall edges and focal points.",
    anchor: "quartzite",
  },
];

export default function Materials() {
  return (
    <div data-testid="materials-page">
      {/* Hero */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-16 md:pb-20">
          <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">
            Sleek Kitchen &amp; Bath Showroom · Countertops
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-5 max-w-4xl leading-[1.05]">
            Quartz, granite &amp; stone — selected in the showroom, fabricated in-house.
          </h1>
          <p className="mt-8 text-zinc-600 text-base md:text-lg leading-relaxed max-w-2xl">
            Compare quartz, granite, marble, quartzite, Dekton, and Neolith side-by-side for
            kitchens, islands, vanity tops, fireplaces, desks, etc.. Every slab is sourced through
            our partnership with Dream Stoneworks and cut at the Hudson, MA fabrication facility.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/showroom"
              className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 px-7 py-4 text-sm hover:border-zinc-950 transition"
              data-testid="materials-hero-cta-showroom"
            >
              Plan a showroom visit
            </Link>
          </div>
        </div>
      </section>

      {/* Material cards — alternating image / text */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 space-y-24 md:space-y-32">
        {MATERIALS.map((m, idx) => (
          <article
            key={m.id}
            id={m.id}
            className="grid md:grid-cols-12 gap-10 md:gap-14 items-center scroll-mt-24"
            data-testid={`material-card-${m.id}`}
          >
            <div className={`md:col-span-7 ${idx % 2 === 1 ? "md:order-last" : ""}`}>
              <Link
                to="/booking?ref=materials&title=Countertop%20Estimate&cat=Countertops"
                className="group block aspect-[4/3] overflow-hidden bg-zinc-100"
                data-testid={`material-image-link-${m.id}`}
                aria-label={`Ask about ${m.name} countertops`}
              >
                <img
                  src={m.image}
                  alt={`${m.name} countertops at the Sleek showroom`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </Link>
              <Link
                to="/booking?ref=materials&title=Countertop%20Estimate&cat=Countertops"
                className="mt-4 inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-zinc-950 border-b border-zinc-300 hover:border-zinc-950 pb-1 transition"
                data-testid={`material-slab-options-${m.id}`}
              >
                Ask about {m.name.toLowerCase()} countertops <ArrowUpRight size={13} />
              </Link>
            </div>
            <div className="md:col-span-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{m.kicker}</div>
              <h2 className="font-display text-3xl md:text-4xl font-light mt-3">{m.name}</h2>
              <p className="font-display text-lg md:text-xl text-zinc-800 mt-5 leading-snug">
                {m.headline}
              </p>
              <p className="text-zinc-600 text-sm md:text-base mt-5 leading-relaxed">{m.blurb}</p>

              <ul className="mt-7 space-y-2.5">
                {m.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-zinc-700">
                    <Check size={16} className="mt-1 shrink-0 text-zinc-950" strokeWidth={2} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                  {m.brandsLabel}
                </div>
                <div className="flex flex-wrap gap-2">
                  {m.brands.map((b) => (
                    <span
                      key={b}
                      className="inline-block border border-zinc-300 text-zinc-700 text-xs tracking-wide uppercase px-3 py-1.5"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              <Link
                to={`/booking?ref=materials-${m.id}&title=${encodeURIComponent(m.name + " Countertop Estimate")}&cat=Countertops`}
                className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-zinc-950 border-b border-zinc-950 pb-1 hover:gap-3 transition-all"
                data-testid={`material-cta-${m.id}`}
              >
                Get a {m.name.toLowerCase()} estimate <ArrowUpRight size={14} />
              </Link>
            </div>
          </article>
        ))}
      </section>

      {/* Best-fit picker */}
      <section className="border-t border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">
            Choosing the right surface
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-light mt-4 max-w-3xl leading-tight">
            Three honest recommendations.
          </h2>
          <p className="mt-6 text-zinc-600 max-w-2xl leading-relaxed">
            Most homeowners narrow it down to quartz, granite, or quartzite. Here&apos;s how we usually
            steer the conversation in the showroom.
          </p>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-14">
            {BEST_FITS.map((b) => (
              <a
                key={b.material}
                href={`#${b.anchor}`}
                className="group block border border-zinc-300 bg-white p-8 md:p-10 hover:border-zinc-950 hover:shadow-[0_8px_30px_-15px_rgba(0,0,0,0.25)] transition"
                data-testid={`best-fit-${b.anchor}`}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{b.label}</div>
                <div className="font-display text-3xl mt-3">{b.material}</div>
                <p className="text-zinc-600 text-sm mt-5 leading-relaxed">{b.note}</p>
                <div className="mt-7 text-xs tracking-[0.25em] uppercase text-zinc-950 flex items-center gap-2 group-hover:gap-3 transition-all">
                  Explore {b.material} <ArrowUpRight size={14} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* In-house fabrication */}
      <section className="border-t border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Factory size={28} strokeWidth={1.3} className="text-zinc-950" />
            <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500 mt-6">
              In-house fabrication
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-light mt-4 leading-tight">
              The slab you pick stays with our team.
            </h2>
          </div>
          <div className="md:col-span-7 text-zinc-600 leading-relaxed space-y-6 text-base md:text-lg">
            <p>
              Once you choose quartz, granite, quartzite, marble, Dekton, or Neolith in the
              Northborough showroom, the project carries through templating, cutting, polishing,
              edge work, and installation planning under one roof.
            </p>
            <ul className="space-y-3 text-sm md:text-base">
              {[
                "Direct seam and slab-layout communication",
                "Professional bridge-saw cuts and diamond-tooled edges",
                "Clear accountability from selection through install",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check size={16} className="mt-1 shrink-0 text-zinc-950" strokeWidth={2} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Visit / CTA */}
      <section className="border-t border-zinc-200 bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.4em] uppercase text-white/60">
              Visit the showroom
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-light mt-4 leading-tight">
              See every slab in person before you commit.
            </h2>
            <p className="mt-6 text-white/75 max-w-xl leading-relaxed">
              Book a private walk-through with our team. Bring photos
              or rough measurements and leave with a practical first estimate.
            </p>
          </div>
          <div className="md:col-span-5 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-white/60 text-xs tracking-[0.25em] uppercase">Showroom</div>
                <div className="mt-1">168 Main Street, Northborough, MA 01532</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={18} className="mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-white/60 text-xs tracking-[0.25em] uppercase">Call</div>
                <div className="mt-1">(781) 760-4524</div>
              </div>
            </div>
            <div className="pt-2 flex flex-wrap gap-3">
              <Link
                to="/booking?ref=materials-footer&title=Showroom%20Tour&cat=Showroom%20Visit"
                className="inline-flex items-center gap-2 bg-white text-zinc-950 px-7 py-4 text-sm hover:bg-zinc-100 transition"
                data-testid="materials-footer-cta-tour"
              >
                Book a private tour <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
